import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SmsRepository } from '../application/SmsRepository'
import {
  campaignDetailSchema,
  campaignListSchema,
  campaignOptionsSchema,
  campaignPreviewSchema,
} from '../domain/smsCampaigns'
import { contractSmsOverviewSchema } from '../domain/contractSmsOverview'
import { clientSmsAlertStatusSchema } from '../domain/smsAlertStatus'
import { consentDetailSchema, consentListSchema, proofOccurredAt, torontoToday } from '../domain/smsConsents'
import {
  consentCollectionPreviewSchema,
  consentInvitationResultSchema,
  consentRequestResultSchema,
} from '../domain/smsConsentCollection'
import { conversationSchema, inboxSchema, linkCandidatesSchema, replyResultSchema } from '../domain/smsInbox'
import { journalEntrySchema, journalPageSchema } from '../domain/smsJournal'
import {
  replyTemplatesSchema,
  templateListSchema,
  templateSchema,
  templateVersionsSchema,
} from '../domain/smsTemplates'
import {
  providerCheckSchema,
  SmsCommandError,
  smsErrorCode,
  smsSettingsOverviewSchema,
  smsTestResultSchema,
} from '../domain/smsSettings'

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Corps d'erreur `{ error: 'CODE' }` renvoyé par une fonction Edge. */
async function functionErrorCode(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (context instanceof Response) {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null
    if (typeof body?.error === 'string') return body.error
  }
  return 'UNEXPECTED'
}

/** Filtre facultatif : une chaîne vide vaut « tous ». */
function nonEmpty(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  return trimmed === '' ? null : trimmed
}

/** Aucun repli sur des fixtures : un faux état d'activation se lirait comme un fait. */
export function createSupabaseSmsRepository(client: AppSupabaseClient): SmsRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, args?: Record<string, unknown>): Promise<unknown> {
    const response = (await rpcClient.rpc(name, args)) as unknown as RpcResponse
    if (response.error) throw new SmsCommandError(smsErrorCode(response.error.message))
    return response.data
  }

  return {
    async getSettingsOverview() {
      return smsSettingsOverviewSchema.parse(await call('sms_settings_overview_v1'))
    },
    async checkProvider() {
      const invocation = await rpcClient.functions.invoke('sms-provider-check', { body: {} })
      if (invocation.error) throw new SmsCommandError(await functionErrorCode(invocation.error))
      return z.object({ check: providerCheckSchema }).parse(invocation.data).check
    },
    async setSendingPaused(paused) {
      return smsSettingsOverviewSchema.parse(await call('sms_set_sending_paused_v1', { p_paused: paused }))
    },
    async requestTestSms(to) {
      return smsTestResultSchema.parse(await call('sms_request_test_v1', { p_to: to }))
    },
    async listJournal(filter) {
      return journalPageSchema.parse(
        await call('sms_journal_v1', {
          p_filter: {
            search: nonEmpty(filter.search),
            state: nonEmpty(filter.state),
            module: nonEmpty(filter.module),
            failuresOnly: filter.failuresOnly ?? false,
            before: filter.before ?? null,
          },
        }),
      )
    },
    async getJournalEntry(id) {
      return journalEntrySchema.parse(await call('sms_journal_entry_v1', { p_outbox_id: id }))
    },
    async listConsents(filter) {
      return consentListSchema.parse(
        await call('sms_consent_list_v1', {
          p_filter: {
            search: nonEmpty(filter.search),
            scope: nonEmpty(filter.scope),
            state: nonEmpty(filter.state),
          },
        }),
      )
    },
    async getConsentDetail(contactId) {
      return consentDetailSchema.parse(await call('sms_consent_detail_v1', { p_contact_id: contactId }))
    },
    async getConsentCollectionPreview() {
      return consentCollectionPreviewSchema.parse(await call('sms_consent_collection_preview_v1'))
    },
    async sendConsentInvitations(idempotencyKey) {
      const invocation = await rpcClient.functions.invoke('sms-consent-invite', { body: { idempotencyKey } })
      if (invocation.error) throw new SmsCommandError(await functionErrorCode(invocation.error))
      return consentInvitationResultSchema.parse(invocation.data)
    },
    async sendConsentRequests(idempotencyKey) {
      return consentRequestResultSchema.parse(
        await call('sms_consent_sms_request_v1', { p_command: { idempotencyKey } }),
      )
    },
    async recordConsentProof(command) {
      return consentDetailSchema.parse(
        await call('sms_record_consent_proof_v1', {
          p_command: {
            phone: command.phone,
            scope: command.scope,
            wordingText: command.wordingText,
            wordingVersion: command.wordingVersion,
            proofNote: command.proofNote,
            occurredAt: proofOccurredAt(command.occurredOn, torontoToday()),
          },
        }),
      )
    },
    async withdrawConsent(command) {
      return consentDetailSchema.parse(await call('sms_withdraw_consent_v1', { p_command: command }))
    },
    async liftBlock(command) {
      return consentDetailSchema.parse(await call('sms_lift_block_v1', { p_command: command }))
    },
    async listInbox(filter) {
      return inboxSchema.parse(
        await call('sms_inbox_v1', { p_filter: { view: filter.view, search: nonEmpty(filter.search) } }),
      )
    },
    async getInboxUnreadCount() {
      return z
        .number()
        .int()
        .parse(await call('sms_inbox_unread_count_v1'))
    },
    async getConversation(conversationId) {
      return conversationSchema.parse(
        await call('sms_conversation_v1', { p_conversation_id: conversationId }),
      )
    },
    async markConversationRead(conversationId) {
      await call('sms_mark_conversation_read_v1', { p_conversation_id: conversationId })
    },
    async setConversationStatus(command) {
      return conversationSchema.parse(await call('sms_set_conversation_status_v1', { p_command: command }))
    },
    async assignConversation(command) {
      return conversationSchema.parse(await call('sms_assign_conversation_v1', { p_command: command }))
    },
    async addConversationNote(command) {
      return conversationSchema.parse(await call('sms_add_conversation_note_v1', { p_command: command }))
    },
    async reply(command) {
      return replyResultSchema.parse(await call('sms_reply_v1', { p_command: command }))
    },
    async searchLinkCandidates(search) {
      return linkCandidatesSchema.parse(await call('sms_link_candidates_v1', { p_search: search }))
    },
    async linkContactClient(command) {
      return conversationSchema.parse(await call('sms_link_contact_client_v1', { p_command: command }))
    },
    async listTemplates() {
      return templateListSchema.parse(await call('sms_templates_v1'))
    },
    async getTemplateVersions(templateId) {
      return templateVersionsSchema.parse(
        await call('sms_template_versions_v1', { p_template_id: templateId }),
      )
    },
    async saveTemplateVersion(command) {
      return templateSchema.parse(await call('sms_save_template_version_v1', { p_command: command }))
    },
    async setTemplateActive(command) {
      return templateSchema.parse(await call('sms_set_template_active_v1', { p_command: command }))
    },
    async listReplyTemplates() {
      return replyTemplatesSchema.parse(await call('sms_reply_templates_v1'))
    },
    async getCampaignOptions(season) {
      return campaignOptionsSchema.parse(await call('sms_campaign_options_v1', { p_season: season }))
    },
    async previewCampaign(command) {
      return campaignPreviewSchema.parse(await call('sms_campaign_preview_v1', { p_command: command }))
    },
    async createCampaign(command) {
      return campaignDetailSchema.parse(
        await call('sms_create_campaign_v1', { p_command: { ...command, confirmed: true } }),
      )
    },
    async cancelCampaign(campaignId) {
      return campaignDetailSchema.parse(await call('sms_cancel_campaign_v1', { p_campaign_id: campaignId }))
    },
    async listCampaigns() {
      return campaignListSchema.parse(await call('sms_campaigns_v1'))
    },
    async getCampaign(campaignId) {
      return campaignDetailSchema.parse(await call('sms_campaign_v1', { p_campaign_id: campaignId }))
    },
    async getContractSmsOverview(contractId) {
      return contractSmsOverviewSchema.parse(
        await call('contract_sms_overview_v1', { p_contract_id: contractId }),
      )
    },
    async getClientSmsAlertStatus(clientIds) {
      if (clientIds.length === 0) return []
      return clientSmsAlertStatusSchema.parse(
        await call('client_sms_alert_status_v1', { p_client_ids: clientIds }),
      )
    },
  }
}
